import React, { FC, FormEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart as ChartJS } from 'chart.js/auto';
import type { ChartConfiguration, ChartDataset, ChartOptions, ScatterDataPoint } from 'chart.js';
/* cspell:disable */
import 'chartjs-adapter-date-fns';
/* cspell:enable */
import { pt } from 'date-fns/locale';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { ResumedStatisticCard } from '@components/molecules/ResumedStatisticCard';
import { ChartCard } from '@components/molecules/ChartCard/ChartCard';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { formatPrice } from '@utils/format';
import { useData } from '@hooks/useData';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { useGetMyAccountDashboardIndicators } from '@services/useGetMyAccountDashboardIndicators';
import { useGetMyAccountDashboardCharts } from '@services/useGetMyAccountDashboardCharts';
import { Form } from '@components/atoms/Form';
import { Select, SelectType } from '@components/atoms/Select';
import { Typography } from '@components/atoms/Typography';
import { Input, InputTypes } from '@components/atoms/Input';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useFilters } from '@hooks/useFilters';
import styles from './Dashboard.module.css';

const STORE_FILTER_ID = 'store-filter';
const START_DATE_FILTER_ID = 'start-date-filter';
const END_DATE_FILTER_ID = 'end-date-filter';
const ALL_STORES_FILTER_VALUE = 'ALL_STORES';

const formatToDateInput = (value?: string): string => {
    if (!value) {
        return '';
    }

    const isoMatch = value.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!isoMatch) {
        return '';
    }

    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
};

const formatDateInputToIso = (value?: string): string => {
    if (!value) {
        return '';
    }

    const [day, month, year] = value.split('/');
    if (!day || !month || !year) {
        return '';
    }

    const isoDate = `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const parsed = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }

    return isoDate;
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
}

const Filters: FC<FiltersProps> = ({ formRef }) => {
    const { t } = useTranslation();
    const { request: requestDashboardIndicators } = useGetMyAccountDashboardIndicators();
    const { request: requestDashboardCharts } = useGetMyAccountDashboardCharts();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const {
        myAccountDashboardIndicators,
        setMyAccountDashboardIndicators,
        setMyAccountDashboardCharts,
        allMyAccountStores,
        isLoadingAllMyAccountStores,
    } = useData();
    const { appliedFilters, setResults, setFilterValues } = useFilters();

    const selectedStoreUuid = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values[0]?.name || '';
    const startDate = appliedFilters.find((item) => item.label === START_DATE_FILTER_ID)?.values[0]?.name || '';
    const endDate = appliedFilters.find((item) => item.label === END_DATE_FILTER_ID)?.values[0]?.name || '';

    const ignoreNextFetchRef = useRef(false);
    const lastFetchKeyRef = useRef<string | null>(null);
    const previousIndicatorsRef = useRef<string | null>(null);

    const [dateError, setDateError] = useState('');

    useEffect(() => {
        return () => {
            setResults([]);
            setMyAccountDashboardIndicators(null);
            setMyAccountDashboardCharts(null);
            ignoreNextFetchRef.current = false;
            lastFetchKeyRef.current = null;
            previousIndicatorsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!allMyAccountStores && !isLoadingAllMyAccountStores) {
            requestAllMyAccountStores();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    useEffect(() => {
        const stores = allMyAccountStores?.data || [];
        if (stores.length === 0 || selectedStoreUuid) {
            return;
        }

        setFilterValues(STORE_FILTER_ID, [{ uuid: ALL_STORES_FILTER_VALUE, name: ALL_STORES_FILTER_VALUE }]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores, selectedStoreUuid]);

    const triggerFetch = useCallback((): boolean => {
        const stores = allMyAccountStores?.data || [];
        const storesUuid =
            selectedStoreUuid === ALL_STORES_FILTER_VALUE
                ? stores
                      .map((store) => store.uuid)
                      .filter(Boolean)
                      .join(',')
                : selectedStoreUuid;

        if (!storesUuid) {
            return false;
        }

        const startAt = formatDateInputToIso(startDate);
        const endAt = formatDateInputToIso(endDate);

        if (startAt && endAt && new Date(startAt) > new Date(endAt)) {
            setDateError('dashboard-component.filters.invalid-range');
            return false;
        }

        setDateError('');

        const payload = {
            stores_uuid: storesUuid,
            ...(startAt ? { start_at: startAt } : {}),
            ...(endAt ? { end_at: endAt } : {}),
        };

        const fetchKey = JSON.stringify(payload);
        if (lastFetchKeyRef.current === fetchKey) {
            return false;
        }

        lastFetchKeyRef.current = fetchKey;

        requestDashboardIndicators(payload);
        requestDashboardCharts(payload);

        return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores, selectedStoreUuid, startDate, endDate]);

    useEffect(() => {
        const start = myAccountDashboardIndicators?.data?.start_at;
        const end = myAccountDashboardIndicators?.data?.end_at;

        const indicatorsKey = JSON.stringify({ start, end });
        if (previousIndicatorsRef.current === indicatorsKey) {
            return;
        }
        previousIndicatorsRef.current = indicatorsKey;

        const formattedStart = formatToDateInput(start);
        if (formattedStart && formattedStart !== startDate) {
            ignoreNextFetchRef.current = true;
            setFilterValues(START_DATE_FILTER_ID, [{ uuid: null, name: formattedStart }]);
        }

        const formattedEnd = formatToDateInput(end);
        if (formattedEnd && formattedEnd !== endDate) {
            ignoreNextFetchRef.current = true;
            setFilterValues(END_DATE_FILTER_ID, [{ uuid: null, name: formattedEnd }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountDashboardIndicators, startDate, endDate]);

    useEffect(() => {
        if (!selectedStoreUuid) {
            return;
        }

        if (ignoreNextFetchRef.current) {
            ignoreNextFetchRef.current = false;
            return;
        }

        triggerFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStoreUuid, startDate, endDate]);

    const submitForm = useCallback(
        (event?: FormEvent) => {
            event?.preventDefault();
            triggerFetch();
        },
        [triggerFetch]
    );

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <>
                    <div className={`d-flex flex-column flex-lg-row align-items-lg-end ${styles['grouped-select-filters']}`}>
                        <StoreFilter />
                        <StartDateFilter />
                        <EndDateFilter />
                    </div>
                    {dateError && (
                        <Typography classes={styles['filters-error']} fontSize="14">
                            {t(dateError)}
                        </Typography>
                    )}
                </>
            </Form>
        </MyAccountContainer.Filters>
    );
};

export const Dashboard: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { t } = useTranslation();
    const {
        myAccountDashboardIndicators,
        isLoadingMyAccountDashboardIndicators,
        myAccountDashboardCharts,
        isLoadingMyAccountDashboardCharts,
    } = useData();

    const formRef = useRef<HTMLFormElement>(null);
    const lineChartRef = useRef<HTMLCanvasElement | null>(null);
    const productsChartRef = useRef<HTMLCanvasElement | null>(null);
    const lineChartInstanceRef = useRef<ChartJS<'line', ScatterDataPoint[]> | null>(null);
    const productsChartInstanceRef = useRef<ChartJS<'bar'> | null>(null);

    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            lineChartInstanceRef.current?.destroy();
            productsChartInstanceRef.current?.destroy();
        };
    }, []);

    const translations = {
        title: t('dashboard-component.title'),
        totalTransactions: t('dashboard-component.statistics.total-transactions'),
        serviceReservations: t('dashboard-component.statistics.service-reservations'),
        visitsCount: t('dashboard-component.statistics.visits-count'),
        visitorsChartTitle: t('dashboard-component.charts.people-per-place'),
        productsChartTitle: t('dashboard-component.charts.products-sold'),
        chartLoading: t('dashboard-component.charts.loading'),
        chartEmpty: t('dashboard-component.charts.empty'),
    };

    const statistics = myAccountDashboardIndicators?.data?.statistics;
    const totalTransactions = statistics?.totalTransactionsPrice ?? 0;
    const serviceReservationsCount = statistics?.serviceReservationsCount ?? 0;
    const visitsCount = statistics?.visitsCount ?? 0;

    const isIndicatorsLoading = isLoadingMyAccountDashboardIndicators;
    const isChartsLoading = isLoadingMyAccountDashboardCharts;

    const totalTransactionsValue = isIndicatorsLoading ? '...' : formatPrice(totalTransactions);
    const serviceReservationsValue = isIndicatorsLoading ? '...' : `${serviceReservationsCount}`;
    const visitsValue = isIndicatorsLoading ? '...' : `${visitsCount}`;

    const peoplePerPlaceSource = myAccountDashboardCharts?.data?.chartDataPeoplePerPlace;
    const productsSoldSource = myAccountDashboardCharts?.data?.chartDataProductsSold;

    const peoplePerPlaceData = useMemo<ScatterDataPoint[]>(() => {
        const source = peoplePerPlaceSource ?? [];

        return source
            .map((item) => {
                const rawX = item?.x;
                const parsedX = typeof rawX === 'number' ? rawX : rawX ? new Date(rawX).getTime() : Number.NaN;

                return {
                    x: parsedX,
                    y: item?.y ?? 0,
                };
            })
            .filter((point) => Number.isFinite(point.x));
    }, [peoplePerPlaceSource]);

    const productsSoldData = useMemo(() => productsSoldSource ?? [], [productsSoldSource]);

    useEffect(() => {
        if (!lineChartRef.current) {
            return;
        }

        lineChartInstanceRef.current?.destroy();

        if (peoplePerPlaceData.length === 0) {
            lineChartInstanceRef.current = null;
            return;
        }

        const options: ChartOptions<'line'> = {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: 'time',
                    adapters: {
                        date: {
                            locale: pt,
                        },
                    },
                    time: {
                        unit: 'day',
                        tooltipFormat: 'dd/MM/yyyy',
                    },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        precision: 0,
                    },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => `${context.parsed.y ?? 0}`,
                    },
                },
            },
        };

        const dataset: ChartDataset<'line', ScatterDataPoint[]> = {
            label: translations.visitorsChartTitle,
            data: peoplePerPlaceData,
            borderColor: '#1a759f',
            backgroundColor: 'rgba(26, 117, 159, 0.15)',
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: '#1a759f',
        };

        const config: ChartConfiguration<'line', ScatterDataPoint[]> = {
            type: 'line',
            data: {
                datasets: [dataset],
            },
            options,
        };

        lineChartInstanceRef.current = new ChartJS<'line', ScatterDataPoint[]>(lineChartRef.current, config);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [peoplePerPlaceData]);

    useEffect(() => {
        if (!productsChartRef.current) {
            return;
        }

        productsChartInstanceRef.current?.destroy();

        if (productsSoldData.length === 0) {
            productsChartInstanceRef.current = null;
            return;
        }

        const labels = productsSoldData.map((item) => String(item?.x ?? ''));
        const values = productsSoldData.map((item) => item?.y ?? 0);

        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    ticks: {
                        maxRotation: 45,
                        minRotation: 0,
                        autoSkip: false,
                    },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        precision: 0,
                    },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => `${context.parsed.y ?? 0}`,
                    },
                },
            },
        };

        productsChartInstanceRef.current = new ChartJS(productsChartRef.current, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: translations.productsChartTitle,
                        data: values,
                        backgroundColor: '#34a0a4',
                        borderRadius: 6,
                    },
                ],
            },
            options,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsSoldData]);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} />
                </div>
                <div className="row row-gap-4 pt-4">
                    <div className="col-12 col-lg-4">
                        <ResumedStatisticCard
                            icon="fa-light fa-money-bill-wave"
                            description={translations.totalTransactions}
                            value={totalTransactionsValue}
                        />
                    </div>
                    <div className="col-6 col-lg-4">
                        <ResumedStatisticCard
                            icon="fa-light fa-calendar-check"
                            description={translations.serviceReservations}
                            value={serviceReservationsValue}
                        />
                    </div>
                    <div className="col-6 col-lg-4">
                        <ResumedStatisticCard
                            icon="fa-light fa-arrow-pointer"
                            description={translations.visitsCount}
                            value={visitsValue}
                        />
                    </div>
                </div>
                <div className="row row-gap-4 pt-4">
                    <div className="col-12">
                        <ChartCard title={translations.visitorsChartTitle}>
                            {isChartsLoading ? (
                                <div className={styles['chart-placeholder']}>{translations.chartLoading}</div>
                            ) : peoplePerPlaceData.length === 0 ? (
                                <div className={styles['chart-placeholder']}>{translations.chartEmpty}</div>
                            ) : (
                                <div className={styles['chart-wrapper']}>
                                    <canvas ref={lineChartRef} />
                                </div>
                            )}
                        </ChartCard>
                    </div>
                    <div className="col-12">
                        <ChartCard title={translations.productsChartTitle}>
                            {isChartsLoading ? (
                                <div className={styles['chart-placeholder']}>{translations.chartLoading}</div>
                            ) : productsSoldData.length === 0 ? (
                                <div className={styles['chart-placeholder']}>{translations.chartEmpty}</div>
                            ) : (
                                <div className={styles['chart-wrapper']}>
                                    <canvas ref={productsChartRef} />
                                </div>
                            )}
                        </ChartCard>
                    </div>
                </div>
            </>
        </MyAccountContainer>
    );
};

const StoreFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const {
        allMyAccountStores,
        isLoadingAllMyAccountStores,
        isLoadingMyAccountDashboardIndicators,
        isLoadingMyAccountDashboardCharts,
    } = useData();
    const { t } = useTranslation();

    const stores = allMyAccountStores?.data || [];

    const options = [
        {
            value: ALL_STORES_FILTER_VALUE,
            text: t('dashboard-component.filters.all-stores'),
        },
        ...stores
            .map((store) => ({
                value: store.uuid || '',
                text: store.name || '',
            }))
            .filter((option) => option.value),
    ];

    const selected = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values[0]?.name || '';

    const handleOnChange = (value: string) => {
        const nextValue = value ? [{ uuid: value, name: value }] : [];
        setFilterValues(STORE_FILTER_ID, nextValue);
    };

    return (
        <div className="d-flex flex-column mb-3 mb-lg-0 me-lg-3">
            <Typography classes="mb-2" fontSize="16">
                {t('dashboard-component.filters.store')}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '220px'}
                type={SelectType.variant2}
                options={options}
                disabled={
                    isLoadingAllMyAccountStores ||
                    isLoadingMyAccountDashboardIndicators ||
                    isLoadingMyAccountDashboardCharts ||
                    stores.length === 0
                }
                value={selected || options[0]?.value || ''}
                onChange={handleOnChange}
            />
        </div>
    );
};

const StartDateFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const selected = appliedFilters.find((item) => item.label === START_DATE_FILTER_ID)?.values[0]?.name || '';
    const inputValue = formatDateInputToIso(selected);

    const handleOnChange = (value: string) => {
        const formattedValue = formatToDateInput(value);
        const filterValue = formattedValue ? [{ uuid: null, name: formattedValue }] : [];
        setFilterValues(START_DATE_FILTER_ID, filterValue);
    };

    return (
        <div className="d-flex flex-column mb-3 mb-lg-0 me-lg-3">
            <Typography classes="mb-2" fontSize="16">
                {t('dashboard-component.filters.start-date')}
            </Typography>
            <Input
                type={InputTypes.date}
                value={inputValue}
                onChange={handleOnChange}
                classes={['xs', 'sm'].includes(pageSize) ? 'w-100' : 'w-auto'}
            />
        </div>
    );
};

const EndDateFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const selected = appliedFilters.find((item) => item.label === END_DATE_FILTER_ID)?.values[0]?.name || '';
    const inputValue = formatDateInputToIso(selected);

    const handleOnChange = (value: string) => {
        const formattedValue = formatToDateInput(value);
        const filterValue = formattedValue ? [{ uuid: null, name: formattedValue }] : [];
        setFilterValues(END_DATE_FILTER_ID, filterValue);
    };

    return (
        <div className="d-flex flex-column mb-3 mb-lg-0 me-lg-3">
            <Typography classes="mb-2" fontSize="16">
                {t('dashboard-component.filters.end-date')}
            </Typography>
            <Input
                type={InputTypes.date}
                value={inputValue}
                onChange={handleOnChange}
                classes={['xs', 'sm'].includes(pageSize) ? 'w-100' : 'w-auto'}
            />
        </div>
    );
};
